import CheckCircle2 from 'lucide-react/dist/esm/icons/check-circle-2'
import FileText from 'lucide-react/dist/esm/icons/file-text'

import { FileDropZone } from '@/components/documents/FileDropZone'
import { UploadProgressRow } from '@/components/documents/UploadProgressRow'
import { UploadedFileRow } from '@/components/documents/UploadedFileRow'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  hasSatisfyingDocument,
  type RiskPortalDocumentRequirement,
  type RiskPortalExternalDocument,
} from '@/types/riskPortal.types'

/** A file that is currently uploading (or failed) for a requirement slot, before it becomes a saved document. */
export interface PendingUpload {
  id: string
  requirementKey: string
  fileName: string
  /** Raw upload progress from the network layer; UI uses time-based animation instead. */
  progress: number
  error?: string
  status?: 'uploading' | 'completing'
  fileSizeBytes?: number
  completedDocument?: RiskPortalExternalDocument
}

interface DocumentRequirementCardProps {
  requirement: RiskPortalDocumentRequirement
  /** Optional 1-based step number shown in the leading badge (required documents only). */
  index?: number
  pendingUploads: PendingUpload[]
  removingIds: Set<string>
  onFilesSelected: (files: File[]) => void
  onRemove: (documentId: string, pendingId?: string) => void
  onRetry: (pendingId: string) => void
  onDismiss: (pendingId: string) => void
  onCancel: (pendingId: string) => void
  onUploadAnimationComplete: (pendingId: string) => void
}

export function DocumentRequirementCard({
  requirement,
  index,
  pendingUploads,
  removingIds,
  onFilesSelected,
  onRemove,
  onRetry,
  onDismiss,
  onCancel,
  onUploadAnimationComplete,
}: DocumentRequirementCardProps) {
  // A slot only reads as "done" when it holds a non-rejected upload — a rejected file must be replaced.
  const hasFiles = hasSatisfyingDocument(requirement.documents)
  const fileCount = requirement.documents.length + pendingUploads.length
  const isFileListScrollable = fileCount > 3
  const inputId = `upload-${requirement.key}`

  return (
    <div
      id={`verification-${requirement.key}`}
      tabIndex={-1}
      className={cn(
        'scroll-mt-24 rounded-2xl border bg-white p-5 shadow-[0_2px_16px_rgba(0,0,0,0.04)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
        hasFiles ? 'border-success-border/40' : 'border-border/60',
      )}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold transition-colors duration-500',
              hasFiles ? 'bg-success-bg/60 text-success-border' : 'bg-primary/10 text-primary',
            )}
          >
            {hasFiles ? (
              <CheckCircle2 className="h-5 w-5" />
            ) : index ? (
              index
            ) : (
              <FileText className="h-5 w-5" />
            )}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold text-foreground">{requirement.label}</p>
              {requirement.optional && (
                <Badge variant="optional" className="text-[10px] uppercase tracking-wide">
                  Optional
                </Badge>
              )}
            </div>
            {requirement.hint ? (
              <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{requirement.hint}</p>
            ) : null}
          </div>
        </div>

        {(requirement.documents.length > 0 || pendingUploads.length > 0) && (
          <div
            className={cn(
              'space-y-2',
              isFileListScrollable &&
                'scrollbar-soft max-h-[14rem] scroll-smooth overflow-y-auto overscroll-y-contain pr-0.5',
            )}
          >
            {requirement.documents.map((document) => (
              <UploadedFileRow
                key={document.id}
                document={document}
                onRemove={() => onRemove(document.id)}
                isRemoving={removingIds.has(document.id)}
              />
            ))}

            {pendingUploads.map((pending) => (
              <UploadProgressRow
                key={pending.id}
                pending={pending}
                onRetry={onRetry}
                onDismiss={onDismiss}
                onCancel={onCancel}
                onAnimationComplete={onUploadAnimationComplete}
                onDeleteDocument={
                  pending.completedDocument
                    ? () => onRemove(pending.completedDocument!.id, pending.id)
                    : undefined
                }
                isDeleting={
                  pending.completedDocument ? removingIds.has(pending.completedDocument.id) : false
                }
              />
            ))}
          </div>
        )}

        <FileDropZone inputId={inputId} onFilesSelected={onFilesSelected} />
      </div>
    </div>
  )
}
